import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    List,
    ListItemButton,
    ListItemText,
    Paper,
    TextField,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import apiService from '../services/api';
import { useDebounce } from '../utils/useDebounce';

export type BrandMergeSource = {
    _id: string;
    name: string;
    totalPaidMentions?: number;
};

type BrandMergeDialogProps = {
    open: boolean;
    onClose: () => void;
    sourceBrand: BrandMergeSource | null;
    onSuccess: (message: string) => void;
    onError: (message: string) => void;
};

const normalizeText = (text: string): string =>
    text
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');

const searchBrands = (brands: { _id: string; name: string; aliases?: string[] }[], query: string) => {
    if (!query.trim()) {
        return brands;
    }

    const normalizedQuery = normalizeText(query);
    return brands.filter((brand) => {
        const brandNameMatch =
            brand.name.toLowerCase().includes(query.toLowerCase()) ||
            normalizeText(brand.name).includes(normalizedQuery);

        const aliasMatch = brand.aliases?.some(
            (alias) =>
                alias.toLowerCase().includes(query.toLowerCase()) ||
                normalizeText(alias).includes(normalizedQuery),
        );

        return brandNameMatch || aliasMatch;
    });
};

export const BrandMergeDialog: React.FC<BrandMergeDialogProps> = ({
    open,
    onClose,
    sourceBrand,
    onSuccess,
    onError,
}) => {
    const queryClient = useQueryClient();
    const [brandSearchQuery, setBrandSearchQuery] = useState('');
    const [selectedBrandId, setSelectedBrandId] = useState('');
    const [selectedBrandName, setSelectedBrandName] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const debouncedSearchQuery = useDebounce(brandSearchQuery, 400);

    useEffect(() => {
        if (open) {
            setBrandSearchQuery('');
            setSelectedBrandId('');
            setSelectedBrandName('');
            setIsSubmitting(false);
        }
    }, [open, sourceBrand?._id]);

    useEffect(() => {
        if (open && debouncedSearchQuery !== brandSearchQuery) {
            queryClient.cancelQueries({ queryKey: ['brands'] });
        }
    }, [debouncedSearchQuery, brandSearchQuery, open, queryClient]);

    const { data: brandsData, isLoading, isFetching } = useQuery({
        queryKey: ['brands', debouncedSearchQuery || 'all'],
        queryFn: () => apiService.getBrands(1, 500, debouncedSearchQuery || undefined),
        enabled: open && !!sourceBrand,
        staleTime: 0,
        gcTime: 0,
        placeholderData: keepPreviousData,
    });

    const filteredBrands = useMemo(() => {
        const brands = (brandsData?.brands || []).filter(
            (brand) => brand._id !== sourceBrand?._id,
        );
        return searchBrands(brands, brandSearchQuery);
    }, [brandSearchQuery, brandsData?.brands, sourceBrand?._id]);

    const handleBrandSelect = (brandId: string, brandName: string) => {
        setSelectedBrandId(brandId);
        setSelectedBrandName(brandName);
        setBrandSearchQuery(brandName);
    };

    const handleClose = () => {
        if (isSubmitting) return;
        onClose();
    };

    const handleConfirm = async () => {
        if (!sourceBrand || !selectedBrandId || !selectedBrandName) return;

        setIsSubmitting(true);
        try {
            const result = await apiService.mergeBrand(sourceBrand._id, selectedBrandId);

            await Promise.all([
                queryClient.invalidateQueries({ queryKey: ['brands-analytics-by-programs'] }),
                queryClient.invalidateQueries({ queryKey: ['brand-category-rankings'] }),
                queryClient.invalidateQueries({ queryKey: ['brands'] }),
            ]);

            onSuccess(
                `Migrated ${result.mentionsUpdated} mention${result.mentionsUpdated === 1 ? '' : 's'} from "${result.sourceName}" to "${result.targetName}" and deleted "${result.sourceName}"`,
            );
            onClose();
        } catch (error: unknown) {
            const message =
                (error as { response?: { data?: { error?: string } } })?.response?.data?.error ||
                'Failed to merge brands. Please try again.';
            onError(message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const mentionCount = sourceBrand?.totalPaidMentions ?? 0;
    const canConfirm = !!selectedBrandId && !!selectedBrandName && !isSubmitting;
    const isSearchPending = brandSearchQuery !== debouncedSearchQuery;
    const isSearching = isFetching || isSearchPending;
    const isInitialLoad = isLoading && !brandsData;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            sx={{ '& .MuiDialog-paper': { height: '80vh', maxHeight: 900 } }}
        >
            <DialogTitle>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="h6">
                        Migrate &quot;{sourceBrand?.name ?? ''}&quot; to another brand
                    </Typography>
                    <IconButton onClick={handleClose} size="small" disabled={isSubmitting}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', pb: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    This will reassign all{' '}
                    <strong>
                        {mentionCount} paid mention{mentionCount === 1 ? '' : 's'}
                    </strong>{' '}
                    from &quot;{sourceBrand?.name}&quot; to the selected brand.
                </Typography>

                <Alert severity="warning" sx={{ mb: 2 }}>
                    &quot;{sourceBrand?.name}&quot; will be added as an alias on the target brand, and
                    the source brand will be permanently deleted. Only <strong>brand_mentions</strong>{' '}
                    are migrated in this operation.
                </Alert>

                <TextField
                    fullWidth
                    label="Search target brand"
                    value={brandSearchQuery}
                    onChange={(e) => {
                        setBrandSearchQuery(e.target.value);
                        if (e.target.value !== selectedBrandName) {
                            setSelectedBrandId('');
                            setSelectedBrandName('');
                        }
                    }}
                    disabled={isSubmitting}
                    helperText={
                        selectedBrandName
                            ? `Selected: ${selectedBrandName}`
                            : 'Type to search brands, then pick one from the list below'
                    }
                    InputProps={{
                        endAdornment: isSearching ? <CircularProgress size={20} /> : undefined,
                    }}
                    sx={{ mb: 2, flexShrink: 0 }}
                />

                <Paper
                    variant="outlined"
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        overflow: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    {isInitialLoad ? (
                        <Box display="flex" justifyContent="center" alignItems="center" flex={1} py={6}>
                            <CircularProgress size={28} />
                        </Box>
                    ) : filteredBrands.length === 0 ? (
                        <Box display="flex" justifyContent="center" alignItems="center" flex={1} py={6}>
                            <Typography variant="body2" color="text.secondary">
                                No brands found
                            </Typography>
                        </Box>
                    ) : (
                        <List dense disablePadding>
                            {filteredBrands.map((brand) => (
                                <ListItemButton
                                    key={brand._id}
                                    onClick={() => handleBrandSelect(brand._id, brand.name)}
                                    selected={selectedBrandId === brand._id}
                                >
                                    <ListItemText
                                        primary={
                                            <Typography variant="body2" fontWeight="bold">
                                                {brand.name}
                                            </Typography>
                                        }
                                        secondary={
                                            brand.aliases && brand.aliases.length > 0 ? (
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ fontStyle: 'italic' }}
                                                >
                                                    Aliases: {brand.aliases.join(', ')}
                                                </Typography>
                                            ) : null
                                        }
                                    />
                                </ListItemButton>
                            ))}
                        </List>
                    )}
                </Paper>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={isSubmitting}>
                    Cancel
                </Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    color="error"
                    disabled={!canConfirm}
                >
                    {isSubmitting
                        ? 'Migrating...'
                        : `Migrate & delete "${sourceBrand?.name ?? ''}"`}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default BrandMergeDialog;
